import React, { useEffect, useState, useRef } from 'react';
import { VideoOff, ScreenShare, Maximize, Minimize, User, User2 } from 'lucide-react';
import { useInterview } from '../../contexts/InterviewContext';
import cn from 'classnames';
import SpeakingIndicator from './SpeakingIndicator';
interface UserVideoProps {
  onClick?: () => void;
  isSmallView?: boolean;
  isUserSpeaking?: boolean; // Used to determine if the user is speaking and we should show the speaking indicator
}

const UserVideo: React.FC<UserVideoProps> = ({
  onClick,
  isSmallView = false,
  isUserSpeaking = false
}) => {
  // State for expanded view (default to true/expanded for user videos)
  // Only use contain mode (false) for screen sharing by default
  const [isExpanded, setIsExpanded] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const playAttemptedRef = useRef(false);
  const playAttemptsRef = useRef(0);
  const MAX_PLAY_ATTEMPTS = 3;
  const lastStreamIdRef = useRef<string | null>(null);
  const [imgError, setImgError] = useState(false);

  const {
    videoRef,
    videoStream,
    isCameraEnabled,
    isScreenSharing,
    cameraError,
    getCameraStatusMessage,
    userData
  } = useInterview();

  // Get profile image and user name from context
  const profileImage = userData?.profileImage || '';
  const userName = userData?.name || 'User';

  // Modified check to show video when either camera is enabled OR screen sharing is active
  const shouldShowVideo = (videoStream || videoRef.current?.srcObject) && 
                        (isCameraEnabled || isScreenSharing) && 
                        videoRef.current && 
                        (
                          (videoRef.current.srcObject instanceof MediaStream && 
                           (videoRef.current.srcObject as MediaStream).getVideoTracks().some(t => 
                             t.readyState === 'live' && t.enabled))
                        );

  // Only show controls in the main view when video is playing
  const showControls = !isSmallView && shouldShowVideo;

  // Toggle between expanded (cover) and collapsed (contain) view
  const toggleExpanded = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent parent onClick from firing
    setIsExpanded(prev => !prev);
  };

  // When switching to screen sharing, default to contain mode for better visibility
  useEffect(() => {
    // Default to contain mode (false) for screen sharing, expanded (true) for camera
    setIsExpanded(!isScreenSharing);
    
    // Reset play attempt counters when screen sharing state changes
    playAttemptedRef.current = false;
    playAttemptsRef.current = 0;
  }, [isScreenSharing]);

  // Enhanced video element connection logic with better error handling
  useEffect(() => {
    // Log the current state for debugging
    console.log('[UserVideo] Stream state:', {
      hasVideoRef: !!videoRef.current,
      hasStream: !!videoStream, 
      streamActive: videoStream?.active,
      hasVideoRefSrcObject: !!videoRef.current?.srcObject,
      isCameraEnabled,
      isScreenSharing,
      currentSrcObject: videoRef.current?.srcObject instanceof MediaStream 
        ? `MediaStream (${(videoRef.current.srcObject as MediaStream).id})` 
        : videoRef.current?.srcObject
    });

    // Check if we have a valid video element to work with
    if (!videoRef.current) {
      console.log('[UserVideo] Missing video reference');
      return;
    }
    
    // Allow video display if either camera is enabled OR screen sharing is active
    if (!isCameraEnabled && !isScreenSharing) {
      console.log('[UserVideo] Neither camera nor screen sharing is enabled, not attaching stream');
      return;
    }

    // If we're screen sharing, the stream might already be attached directly
    if (isScreenSharing && videoRef.current.srcObject instanceof MediaStream) {
      const screenStream = videoRef.current.srcObject as MediaStream;
      const screenStreamId = screenStream.id;
      const videoTracks = screenStream.getVideoTracks();
      const hasVideoTracks = videoTracks.length > 0;
      const hasLiveVideoTracks = videoTracks.some(t => t.readyState === 'live' && t.enabled);
      
      // Track if the stream has changed
      const isNewStream = lastStreamIdRef.current !== screenStreamId;
      if (isNewStream) {
        console.log(`[UserVideo] Stream changed: ${lastStreamIdRef.current} -> ${screenStreamId}`);
        lastStreamIdRef.current = screenStreamId;
        // Reset play attempts for new stream
        playAttemptedRef.current = false;
        playAttemptsRef.current = 0;
      }
      
      if (hasLiveVideoTracks) {
        console.log('[UserVideo] Screen sharing stream already attached with live tracks:', 
          videoTracks.map(t => `${t.label} (${t.readyState})`));
          
        // Try to play the video if not already playing
        if (videoRef.current.paused && (!playAttemptedRef.current || isNewStream)) {
          playAttemptedRef.current = true;
          
          videoRef.current.play()
            .then(() => {
              console.log('[UserVideo] Screen sharing video playback started successfully');
              playAttemptsRef.current = 0;
            })
            .catch(err => {
              console.error('[UserVideo] Error playing screen sharing video:', err);
              
              // Try again with a delay if we haven't reached the max attempts
              if (playAttemptsRef.current < MAX_PLAY_ATTEMPTS) {
                playAttemptsRef.current++;
                
                setTimeout(() => {
                  if (videoRef.current && videoRef.current.srcObject) {
                    console.log(`[UserVideo] Retrying screen sharing video playback (attempt ${playAttemptsRef.current})...`);
                    
                    videoRef.current.play()
                      .then(() => console.log('[UserVideo] Retry successful'))
                      .catch(retryErr => console.error('[UserVideo] Retry failed:', retryErr));
                  }
                }, 300);
              }
            });
        }
        
        return; // Screen sharing stream is already set up
      } else if (hasVideoTracks) {
        console.log('[UserVideo] Screen sharing stream has tracks but none are live:', 
          videoTracks.map(t => `${t.label} (${t.readyState})`));
      }
    }
    
    // For normal camera usage or when screen stream needs to be attached
    if (videoStream && (isCameraEnabled || isScreenSharing)) {
      const streamId = videoStream.id;
      const isNewStream = lastStreamIdRef.current !== streamId;
      
      if (isNewStream) {
        console.log(`[UserVideo] New stream to attach: ${lastStreamIdRef.current} -> ${streamId}`);
        lastStreamIdRef.current = streamId;
        // Reset play attempts for new stream
        playAttemptedRef.current = false;
        playAttemptsRef.current = 0;
      }
      
      // Force attach the stream to the video element if it's different
      if (videoRef.current.srcObject !== videoStream || isNewStream) {
        console.log('[UserVideo] Attaching stream to video element');
        
        // In some cases, we need to clean up an existing srcObject first
        if (videoRef.current.srcObject && videoRef.current.srcObject !== videoStream) {
          const oldStream = videoRef.current.srcObject as MediaStream;
          console.log('[UserVideo] Removing old stream before attaching new one');
          
          // Stop old tracks if they're not used elsewhere
          try {
            const oldVideoTracks = oldStream.getVideoTracks();
            oldVideoTracks.forEach(track => {
              // Only stop if not shared with the new stream
              const isSharedTrack = videoStream.getVideoTracks().some(t => t.id === track.id);
              if (!isSharedTrack) {
                console.log(`[UserVideo] Stopping old track: ${track.label}`);
                track.stop();
              }
            });
          } catch (err) {
            console.warn('[UserVideo] Error cleaning up old tracks:', err);
          }
        }
        
        // Set the new stream
        videoRef.current.srcObject = videoStream;
        playAttemptedRef.current = true;
        
        // Try to play the video and handle any errors
        videoRef.current.play()
          .then(() => {
            console.log('[UserVideo] Video playback started successfully');
            playAttemptsRef.current = 0;
          })
          .catch(err => {
            console.error('[UserVideo] Error playing video:', err);
            
            // Try once more with a slight delay
            if (playAttemptsRef.current < MAX_PLAY_ATTEMPTS) {
              playAttemptsRef.current++;
              
              setTimeout(() => {
                if (videoRef.current && videoStream) {
                  console.log(`[UserVideo] Retrying video playback (attempt ${playAttemptsRef.current})...`);
                  
                  videoRef.current.play()
                    .then(() => console.log('[UserVideo] Retry successful'))
                    .catch(retryErr => console.error('[UserVideo] Retry failed:', retryErr));
                }
              }, 300);
            }
          });
      }

    }
  }, [videoRef, videoStream, isCameraEnabled, isScreenSharing]);

  // Effect to handle keyboard shortcuts
  useEffect(() => {
    // Only add keyboard shortcuts when in main view and video is showing
    if (isSmallView || !shouldShowVideo) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Avoid triggering when user is typing in inputs
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (e.key === 'e') { // Toggle expand/collapse with 'e' key
        setIsExpanded(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSmallView, shouldShowVideo]);

  return (
    <div
      className={`relative ${isSmallView ? 'w-full h-full' : 'w-full h-full'} ${isScreenSharing && !isSmallView ? 'fade-zoom-in' : 'fade-zoom-in'}`}
      onClick={onClick}
    >
      {/* Screen sharing container with proper background */}
      <div 
        ref={containerRef}
        className={cn(
          "relative w-full h-full flex items-center justify-center", 
          {
            "bg-gray-900 p-2": isScreenSharing && !isSmallView && !isExpanded, // Add padding only in full screen mode when not expanded
            "overflow-hidden": true
          }
        )}
      >
        {/* Video element */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={cn("w-full", {
            "h-full": isSmallView || isExpanded,
            "max-h-[calc(100%-8px)]": !isSmallView && !isExpanded, // Adjust height for padding when not expanded
            "rounded-lg": isSmallView,
            "hidden": !shouldShowVideo,
            "mirrored": isCameraEnabled && !isScreenSharing,
            "non-mirrored": isScreenSharing,
            // Apply object-fit based on expanded state, not view type
            "object-contain": !isExpanded,
            "object-cover": isExpanded
          })}
        />
      </div>
      
      {/* View control buttons (only shown in main view when video is visible) */}
      {showControls && (
        <div className="absolute top-3 right-3 flex items-center space-x-2 z-20">
          {/* Toggle expand/collapse button */}
          <button 
            onClick={toggleExpanded}
            className="p-2 bg-black/70 rounded-lg backdrop-blur-sm text-white hover:bg-black/90 transition-colors"
            title={isExpanded ? "Fit to screen (contain) [Press E]" : "Fill screen (cover) [Press E]"}
          >
            {isExpanded ? <Minimize size={18} /> : <Maximize size={18} />}
          </button>
        </div>
      )}
      
      {/* Screen sharing indicator overlay */}
      {shouldShowVideo && isScreenSharing && (
        <div className={`absolute top-2 ${isSmallView ? 'left-2' : 'left-4'} bg-surface-light dark:bg-surface-dark rounded-lg backdrop-blur-sm px-3 py-1.5 
                        text-text-primary-light dark:text-text-primary-dark flex items-center gap-2 z-10 text-xs ${isSmallView ? 'scale-75 origin-top-left' : ''}`}>
          <ScreenShare size={16} className="text-primary-light dark:text-primary-dark" />
          <span>{isSmallView ? 'Screen' : 'Screen sharing'}</span>
        </div>
      )}
      
      {/* Fallback content when video is not available */}
      {!shouldShowVideo && (
        <div className={`absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-background-light to-surface-light dark:from-background-dark dark:to-surface-dark ${isSmallView ? 'rounded-lg' : ''}`}>
          {/* Avatar rendering */}
          {!imgError ? (
            <img
              src={profileImage}
              alt={""}
              className={`${isSmallView ? 'w-14 h-14' : 'w-28 h-28 md:w-32 md:h-32'} rounded-full object-cover ${isSmallView ? 'mb-1' : 'border-4 border-white/10'}`}
              onError={() => setImgError(true)}
            />
          ) : (
            <div className={`${isSmallView ? 'w-14 h-14 mb-1' : 'w-28 h-28 md:w-32 md:h-32 border-4 border-white/10'} rounded-full flex items-center justify-center bg-secondary-light dark:bg-secondary-dark/70`}>
              <span className={`${isSmallView ? 'text-2xl' : 'text-5xl md:text-6xl'} text-white font-bold font-heading`}>{userName?.charAt(0).toUpperCase()}</span>
            </div>
          )}
          {!isSmallView && (
                <p className="text-text-primary-light dark:text-text-primary-dark text-lg mt-3 font-semibold font-heading">{userName.charAt(0).toUpperCase() + userName.slice(1)}</p>
              )}
        </div>
      )}

      {/* User speaking indicator */}
      {isUserSpeaking && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
          <SpeakingIndicator type="user" isSmallView={isSmallView} />
        </div>
      )}
    </div>
  );
};

export default UserVideo; 